import { RdpServerMessage, RdpSession, type RdpClientMessage } from "@cloudserver/contracts";
import { useSyncExternalStore } from "react";
import { api, ApiProblem } from "../api/client.js";
import { PANE_PADDING } from "../terminal/geometry.js";
import { screenSettings } from "../terminal/screen-settings.js";
import { rememberRdpUser, terminals } from "../terminal/store.js";
import { loadGuacamole, type GuacClient, type GuacKeyboard } from "./guacamole.js";
import { RdpTunnel } from "./tunnel.js";

/*
 * One remote desktop per tab, outside React like the terminals
 * (`terminal/connections.ts`): a Guacamole client over our WebSocket. The
 * display lives in its own element, moved between the workspace and a
 * hidden parking spot, so the session runs on while the person looks
 * elsewhere. Credentials go once into the start message and are kept
 * nowhere; a reconnect asks for the password again.
 */

const socketUrl = (path: string) =>
  `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${path}`;

/** Keysyms the toolbar sends. */
export const KEYSYM = {
  ctrl: 0xffe3,
  alt: 0xffe9,
  delete: 0xffff,
  win: 0xffeb,
  tab: 0xff09,
  enter: 0xff0d,
} as const;

/** The display sizes the API accepts (`RdpClientMessage`). */
const SIZE = { minWidth: 320, minHeight: 200, max: 8192 } as const;
/** Resizing a window sends many sizes: the desktop follows the last one. */
const RESIZE_DELAY_MS = 300;
/** The time zones `RdpClientMessage` accepts. */
const TIME_ZONE = /^[A-Za-z0-9_+\-/]{1,64}$/;

export interface RdpCredentials {
  username: string;
  password: string;
  domain?: string;
}

/** Where desktops wait while no workspace shows them. */
function parking(): HTMLElement {
  let element = document.getElementById("rdp-parking");
  if (!element) {
    element = document.createElement("div");
    element.id = "rdp-parking";
    element.setAttribute("aria-hidden", "true");
    element.inert = true;
    Object.assign(element.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: "1024px",
      height: "768px",
      visibility: "hidden",
      overflow: "hidden",
    });
    document.body.append(element);
  }
  return element;
}

const clampSize = (width: number, height: number) => ({
  width: Math.min(SIZE.max, Math.max(SIZE.minWidth, Math.round(width))),
  height: Math.min(SIZE.max, Math.max(SIZE.minHeight, Math.round(height))),
});

const safeJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export class RdpConnection {
  /** Focusable host of the display: keyboard input goes to the desktop while it has focus. */
  readonly element = document.createElement("div");
  private client: GuacClient | null = null;
  private keyboard: GuacKeyboard | null = null;
  private socket: WebSocket | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private clipboard = "";
  private readonly listeners = new Set<() => void>();

  private readonly stopScale: () => void;

  constructor(readonly id: string) {
    this.element.className = "relative flex h-full w-full items-center justify-center outline-none";
    this.element.tabIndex = 0;
    this.element.dataset.rdp = id;
    this.element.addEventListener("blur", () => this.keyboard?.reset());
    parking().append(this.element);
    // «Вписать / 1:1» applies at once to open desktops.
    this.stopScale = screenSettings.subscribe(() => this.fit());
  }

  /** Text the remote desktop last put on its clipboard. */
  get remoteClipboard(): string {
    return this.clipboard;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Opens a new session and signs in with these credentials. */
  async connect(credentials: RdpCredentials): Promise<void> {
    const tab = terminals.tab(this.id);
    if (!tab || this.disposed) return;
    this.teardown();
    terminals.update(this.id, {
      status: "connecting",
      failure: null,
      problem: null,
      unixUser: credentials.username,
    });
    let session: RdpSession;
    let guacamole: Awaited<ReturnType<typeof loadGuacamole>>;
    try {
      [session, guacamole] = await Promise.all([
        api(`/projects/${tab.projectId}/servers/${tab.serverId}/rdp`, {
          method: "POST",
          body: {},
          schema: RdpSession,
        }),
        loadGuacamole(),
      ]);
    } catch (error) {
      terminals.update(this.id, {
        status: "disconnected",
        failure: null,
        problem:
          error instanceof ApiProblem && error.problem
            ? (error.problem.reason ?? error.problem.code)
            : "unexpected",
      });
      return;
    }
    if (this.disposed) return;
    rememberRdpUser(tab.serverId, credentials.username);
    terminals.update(this.id, { sessionId: session.sessionId });

    const socket = new WebSocket(socketUrl(session.url));
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    const tunnel = new RdpTunnel(socket);
    const client = new guacamole.Client(tunnel);
    this.client = client;
    const display = client.getDisplay();
    const screen = display.getElement();
    this.element.replaceChildren(screen);
    display.onresize = () => this.fitScale();
    // The desktop's own pointer image, as the cursor over it (a data URL: CSP img-src allows it).
    display.oncursor = (canvas, x, y) => {
      screen.style.cursor = `url(${canvas.toDataURL()}) ${x} ${y}, default`;
    };

    const mouse = new guacamole.Mouse(screen);
    const touch = new guacamole.Mouse.Touchscreen(screen);
    const pointer = (event: { state: Parameters<GuacClient["sendMouseState"]>[0] }) => {
      if (document.activeElement !== this.element) this.element.focus({ preventScroll: true });
      client.sendMouseState(event.state, true);
    };
    mouse.onEach(["mousedown", "mousemove", "mouseup"], pointer);
    touch.onEach(["mousedown", "mousemove", "mouseup"], pointer);
    // One keyboard for the element's life: it listens on the element, whichever client is current.
    if (!this.keyboard) {
      const keyboard = new guacamole.Keyboard(this.element);
      keyboard.onkeydown = (keysym) => {
        this.client?.sendKeyEvent(1, keysym);
        return false;
      };
      keyboard.onkeyup = (keysym) => this.client?.sendKeyEvent(0, keysym);
      this.keyboard = keyboard;
    }

    client.onclipboard = (stream, mimetype) => {
      if (mimetype !== "text/plain") return;
      const reader = new guacamole.StringReader(stream);
      let text = "";
      reader.ontext = (part) => (text += part);
      reader.onend = () => {
        this.clipboard = text;
        for (const listener of this.listeners) listener();
        // Browsers allow this only while the page has focus; the toolbar shows it otherwise.
        void navigator.clipboard?.writeText(text).catch(() => undefined);
      };
    };

    socket.addEventListener("open", () => {
      const size = this.size();
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // The layout chosen in «Настройки сеанса»; it applies from the next sign-in.
      const layout = screenSettings.snapshot().rdpLayout;
      const start: RdpClientMessage = {
        type: "start",
        ...size,
        dpi: 96,
        username: credentials.username,
        password: credentials.password,
        ...(credentials.domain ? { domain: credentials.domain } : {}),
        // The Windows clock follows the browser's zone, when it is an IANA name the API takes.
        ...(TIME_ZONE.test(zone) ? { timezone: zone } : {}),
        ...(layout !== "default" ? { layout } : {}),
      };
      socket.send(JSON.stringify(start));
      client.connect();
    });
    socket.addEventListener("message", (event: MessageEvent<ArrayBuffer | string>) => {
      if (typeof event.data !== "string") {
        tunnel.receive(event.data);
        return;
      }
      const parsed = RdpServerMessage.safeParse(safeJson(event.data));
      if (!parsed.success) return;
      if (parsed.data.type === "ready") {
        terminals.update(this.id, { status: "connected" });
        this.fitScale();
        this.element.focus({ preventScroll: true });
      } else {
        terminals.update(this.id, { status: "disconnected", failure: parsed.data.reason });
      }
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      tunnel.disconnect();
      const current = terminals.tab(this.id);
      if (current && current.status !== "disconnected") {
        terminals.update(this.id, {
          status: "disconnected",
          failure: current.status === "connected" ? "closed" : (current.failure ?? "unreachable"),
        });
      }
    });
  }

  /**
   * The space the desktop has, in CSS pixels: with «Вписать» inside the
   * same even padding as a terminal's grid (`PANE_PADDING`), letterboxed in
   * the screen's colour; with «1:1» the whole box, which scrolls.
   */
  private inner(): { width: number; height: number } {
    const box = this.element.getBoundingClientRect();
    const pad = screenSettings.snapshot().scale === "actual" ? 0 : PANE_PADDING;
    return {
      width: Math.max(0, box.width - 2 * pad),
      height: Math.max(0, box.height - 2 * pad),
    };
  }

  /** The size of the space the desktop has, in CSS pixels. */
  private size(): { width: number; height: number } {
    const box = this.inner();
    return clampSize(box.width || 1024, box.height || 768);
  }

  /**
   * «Вписать»: the desktop follows the space it has (guacd's
   * `display-update`) and is scaled down until it does. «1:1»: it keeps its
   * size, pixel for pixel, and the space scrolls.
   */
  fit(): void {
    this.fitScale();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (screenSettings.snapshot().scale === "actual") return;
    this.resizeTimer = setTimeout(() => {
      if (!this.client || terminals.tab(this.id)?.status !== "connected") return;
      if (this.element.parentElement === parking()) return;
      const { width, height } = this.size();
      this.client.sendSize(width, height);
    }, RESIZE_DELAY_MS);
  }

  private fitScale(): void {
    const display = this.client?.getDisplay();
    const actual = screenSettings.snapshot().scale === "actual";
    this.element.classList.toggle("overflow-auto", actual);
    this.element.classList.toggle("items-center", !actual);
    this.element.classList.toggle("justify-center", !actual);
    this.element.style.padding = actual ? "" : `${PANE_PADDING}px`;
    if (!display || display.getWidth() === 0) return;
    if (actual) {
      display.scale(1);
      return;
    }
    const box = this.inner();
    if (box.width === 0) return;
    display.scale(Math.min(1, box.width / display.getWidth(), box.height / display.getHeight()));
  }

  /** Presses the keys together and lets them go in reverse order (Ctrl+Alt+Del). */
  combo(keysyms: readonly number[]): void {
    if (!this.client) return;
    for (const keysym of keysyms) this.client.sendKeyEvent(1, keysym);
    for (const keysym of [...keysyms].reverse()) this.client.sendKeyEvent(0, keysym);
    this.focus();
  }

  /** One key from the on-screen keyboard, with the modifiers held there. */
  press(keysym: number, held: readonly number[] = []): void {
    this.combo([...held, keysym]);
  }

  /**
   * Types text key by key («Набрать текст»): each character as its X11
   * keysym (Unicode ones above Latin-1), a line break as Return.
   */
  typeText(text: string): void {
    for (const char of text) {
      const code = char.codePointAt(0) ?? 0;
      if (char === "\n") this.combo([KEYSYM.enter]);
      else if (char === "\t") this.combo([KEYSYM.tab]);
      else if (code >= 0x20 && code !== 0x7f) this.combo([code <= 0xff ? code : 0x01000000 | code]);
    }
  }

  /** Puts text on the remote desktop's clipboard (paste it there with Ctrl+V). */
  sendClipboard(text: string): void {
    const client = this.client;
    if (!client || terminals.tab(this.id)?.status !== "connected") return;
    void loadGuacamole().then((guacamole) => {
      const writer = new guacamole.StringWriter(client.createClipboardStream("text/plain"));
      writer.sendText(text);
      writer.sendEnd();
    });
  }

  focus(): void {
    this.element.focus({ preventScroll: true });
  }

  /** Shows the desktop in `pane`, or parks it when null. */
  place(pane: HTMLElement | null): void {
    (pane ?? parking()).append(this.element);
    requestAnimationFrame(() => this.fit());
  }

  /** Ends the session (the person signs in again to reconnect). */
  end(): void {
    this.teardown();
    terminals.update(this.id, { status: "disconnected", failure: "closed" });
  }

  private teardown(): void {
    const socket = this.socket;
    this.socket = null;
    // Held keys are let go on the desktop before it disconnects.
    this.keyboard?.reset();
    this.client?.disconnect();
    this.client = null;
    socket?.close();
    this.element.replaceChildren();
  }

  /** The desktop as one picture, for the assistant's screen context; null before it drew. */
  snapshot(): HTMLCanvasElement | null {
    const display = this.client?.getDisplay();
    if (!display || display.getWidth() === 0) return null;
    return display.flatten();
  }

  dispose(): void {
    this.disposed = true;
    this.stopScale();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.teardown();
    this.element.remove();
    this.listeners.clear();
  }
}

const connections = new Map<string, RdpConnection>();

export const rdpConnectionOf = (id: string): RdpConnection | undefined => connections.get(id);

/** The remote desktop's clipboard text of a tab, for the toolbar. */
export function useRemoteClipboard(id: string): string {
  const connection = connections.get(id);
  return useSyncExternalStore(
    connection?.subscribe ?? (() => () => undefined),
    () => connection?.remoteClipboard ?? "",
  );
}

export interface OpenRdpRequest {
  projectId: string;
  server: { id: string; name: string };
  credentials: RdpCredentials;
}

/** Opens a remote desktop tab (with the Windows icon) and signs in. */
export function openRdp(request: OpenRdpRequest): string {
  const id = crypto.randomUUID();
  terminals.add({
    id,
    kind: "rdp",
    projectId: request.projectId,
    serverId: request.server.id,
    serverName: request.server.name,
    osFlavor: "windows",
    osVersion: null,
    unixUser: request.credentials.username,
    auth: "password",
    status: "connecting",
    failure: null,
    offeredHostKey: null,
    problem: null,
    hostKeyFingerprint: null,
    sessionId: null,
    unseen: false,
  });
  const connection = new RdpConnection(id);
  connections.set(id, connection);
  void connection.connect(request.credentials);
  return id;
}

/**
 * A remote desktop tab rebuilt after a reload (`terminal/restore.ts`): its
 * display, waiting for the person to sign in again (the password is never kept).
 */
export function resumeRdp(id: string): void {
  if (connections.has(id)) return;
  connections.set(id, new RdpConnection(id));
}

/** Ends a tab's remote desktop, if it is one (closing the tab: `terminal/connections.ts`). */
export function disposeRdp(id: string): void {
  connections.get(id)?.dispose();
  connections.delete(id);
}
